import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import type { ResourceDictionary } from "Infrastructure/Translations/ResourceDictionary";
import { Resources } from "Infrastructure/Translations/Resources";
import { addMonths, startOfMonth } from "date-fns";
import { format } from "date-fns";
import type { ObjPathProxy } from "ts-object-path";
import { type ObjectSchema, bool, date, object, string } from "yup";
import { number } from "../Utils/schemaUtils";

export type FinancialReserveGoalForm = {
  readonly name: string;
  readonly currentAmount: number;
  readonly targetAmount: number;
  readonly targetDate: Date;
  readonly referenceStartDate: Date;
  readonly monthlyContribution: number;
  readonly monthlyContributionWithFee: number;
  readonly interestRate: number;
  readonly inflationRate: number;
  readonly feeRate: number;
  readonly includeInflation: boolean;
  readonly includeFee: boolean;
  readonly note?: string;
  readonly priority?: boolean;
};

export const minDate = startOfMonth(addMonths(new Date(), 1));

export const financialReserveFormSchema: (
  t: (resourcePath: ObjPathProxy<ResourceDictionary, string>, options?: any) => string,
) => ObjectSchema<FinancialReserveGoalForm> = (t) => {
  return object({
    name: string().required(t(Resources.Validation.Required)),
    referenceStartDate: date().required(t(Resources.Validation.Required)),
    currentAmount: number().required(t(Resources.Validation.Required)),
    targetAmount: number()
      .required(t(Resources.Validation.Required))
      .when("currentAmount", (curr, schema) => {
        const minVal = typeof curr === "number" && Number.isFinite(curr) ? curr : 0;
        return schema.min(minVal, t(Resources.Validation.Number.Min, { count: minVal }));
      }),
    targetDate: date()
      .required(t(Resources.Validation.Required))
      .min(minDate, t(Resources.Validation.MustBeOlderThanDate, { date: format(minDate, "dd.MM.yyyy") })),
    monthlyContribution: number().required(t(Resources.Validation.Required)),
    monthlyContributionWithFee: number().required(t(Resources.Validation.Required)),
    feeRate: number().required(t(Resources.Validation.Required)),
    interestRate: number().required(t(Resources.Validation.Required)),
    inflationRate: number().required(t(Resources.Validation.Required)),
    includeInflation: bool().required(t(Resources.Validation.Required)),
    includeFee: bool().required(t(Resources.Validation.Required)),
    note: string().max(NOTE_MAX_LENGTH, t(Resources.Validation.String.Max, { count: NOTE_MAX_LENGTH })),
    priority: bool(),
  }).defined();
};
